import { CalendarOutlined, CheckCircleOutlined, DeleteOutlined, EditOutlined, HistoryOutlined } from '@ant-design/icons'
import type { BaseFreightRate } from '@api/useFreight/types'
import { BBDTag, GraviButton, Horizontal, MaterialIcon, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Dropdown, Popconfirm, Popover, Tabs, type TabsProps } from 'antd'
import dayjs from 'dayjs'

import { RateUpdateInfo } from '@components/Freight/Ratebooks/Drill-in/components/RateUpdateInfo'
import { EffectiveDateForm } from './EffectiveDateForm'

interface RateScheduleTabsProps {
  sortedHistory: BaseFreightRate[]
  selectedRate: BaseFreightRate | null
  setSelectedRate: (rate: BaseFreightRate | null) => void
  setIsEditMode: (isEdit: boolean) => void
  handleDeleteRate: (id: string) => Promise<void>
  isChangeEffectivePopoverOpen: boolean
  setIsChangeEffectivePopoverOpen: (isOpen: boolean) => void
  handleUpdateEffectiveDate?: (rate: BaseFreightRate, dateInfo: string) => Promise<void>
  saveLoading?: boolean
}

export function RateScheduleTabs({
  sortedHistory,
  selectedRate,
  setSelectedRate,
  setIsEditMode,
  handleDeleteRate,
  isChangeEffectivePopoverOpen,
  setIsChangeEffectivePopoverOpen,
  handleUpdateEffectiveDate,
  saveLoading,
}: RateScheduleTabsProps) {
  // Create tab items for the rate history
  const items = sortedHistory.map((rate) => {
    const effectiveFrom = dayjs(rate.effective_from)
    const now = dayjs()

    // compute isExpired
    const isExpired = rate.status !== 'Active' && rate.status !== 'Scheduled'

    // Create the label with optional ellipsis menu
    const label = (
      <Horizontal style={{ overflow: 'visible', width: '100%' }} flex='0' gap={16} verticalCenter key={rate.id}>
        <Popover
          content={<RateUpdateInfo rate={rate} />}
          title='Rate Schedule History'
          trigger='hover'
          placement='left'
        >
          <BBDTag
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              padding: '0 4px',
            }}
          >
            <MaterialIcon icon='history' size={14} />
          </BBDTag>
        </Popover>
        <Horizontal flex='0' style={{ overflow: 'visible' }}>
          {rate.status === 'Active' && <CheckCircleOutlined className='mr-0 pr-3' />}
          {rate.status === 'Scheduled' && <CalendarOutlined className='mr-0 pr-3 mt-0' style={{ paddingBottom: 2 }} />}
          {isExpired && <HistoryOutlined className='mr-0 pr-3' />}
        </Horizontal>
        <Horizontal flex={1} gap={4}>
          <Texto>{effectiveFrom.format('MM/DD/YY')}</Texto>
          <Texto appearance='hint'>-</Texto>
          <Texto>{rate.effective_to ? dayjs(rate.effective_to).format('MM/DD/YY') : 'Indefinite'}</Texto>
        </Horizontal>

        <Dropdown
          menu={{
            items: [
              {
                key: 'edit',
                label: 'Edit Rates',
                icon: <EditOutlined />,
                onClick: ({ domEvent }) => {
                  domEvent.stopPropagation()
                  // Set edit mode and hide tab bar
                  setIsEditMode(true)
                  setSelectedRate(rate)
                },
              },
              {
                key: 'change_effective',
                label: 'Change Effective',
                icon: <CalendarOutlined />,
                onClick: ({ domEvent }) => {
                  domEvent.stopPropagation()
                  // Open popover for changing effective date
                  setIsChangeEffectivePopoverOpen(true)
                  // Set selected rate to ensure the popover has the correct context
                  setSelectedRate(rate)
                },
              },
              {
                key: 'delete',
                label: (
                  <Popconfirm
                    title='Delete Rate'
                    description='Are you sure you want to delete this rate?'
                    onConfirm={async (e) => {
                      e?.stopPropagation()
                      await handleDeleteRate(rate.id)
                    }}
                    onCancel={(e) => {
                      e?.stopPropagation()
                    }}
                    okText='Yes'
                    cancelText='No'
                    okButtonProps={{ danger: true }}
                  >
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                      onClick={(e) => e.stopPropagation()}
                      onKeyUp={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      onKeyPress={(e) => e.stopPropagation()}
                    >
                      <DeleteOutlined />
                      <span>Delete</span>
                    </div>
                  </Popconfirm>
                ),
                danger: true,
              },
            ],
          }}
          trigger={['click']}
          placement='bottomRight'
        >
          <GraviButton
            size='small'
            style={{ overflow: 'visible', flex: '0', minWidth: 32 }}
            appearance='outlined'
            icon={<MaterialIcon className='pt-1' icon='edit' />}
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
            }}
          />
        </Dropdown>

        <Popover
          open={isChangeEffectivePopoverOpen && selectedRate?.id === rate.id}
          onOpenChange={(open) => {
            if (!open) setIsChangeEffectivePopoverOpen(false)
          }}
          trigger='click'
          title='Change Effective Date'
          content={
            <EffectiveDateForm
              rate={rate}
              sortedHistory={sortedHistory}
              loading={saveLoading}
              onFinish={(dateInfo) => {
                if (dateInfo === 'cancel') {
                  setIsChangeEffectivePopoverOpen(false)
                  return
                }
                // Call mutation to update only the effective date
                if (handleUpdateEffectiveDate) {
                  handleUpdateEffectiveDate(rate, dateInfo)
                }
              }}
            />
          }
          placement='right'
          arrow
        >
          {/* Invisible trigger element, popover controlled by state */}
          <div style={{ position: 'absolute', width: 0, height: 0 }} />
        </Popover>
      </Horizontal>
    )

    return {
      key: rate.id,
      label,
    }
  })

  return (
    <Vertical flex='0' style={{ overflow: 'visible', minWidth: 300 }}>
      <Vertical flex='0' style={{ overflow: 'visible' }} bg='elevated' className='p-3' border='all' borderRadius='4px'>
        <Texto category='p2'>Rate Schedule</Texto>
      </Vertical>
      <Tabs
        size='small'
        tabPosition='left'
        tabBarGutter={0}
        tabBarStyle={{
          background: 'var(--bg-1)',
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
        }}
        style={{ minHeight: '100%', width: '100%', overflowY: 'auto' }}
        items={items}
        activeKey={selectedRate?.id || undefined}
        onChange={(key) => {
          setSelectedRate(sortedHistory.find((rate) => rate.id === key) || null)
        }}
      />
    </Vertical>
  )
}
